import { createElement } from 'react'
import parse, { attributesToProps, domToReact } from 'html-react-parser'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../../constants/common'
import { HTML_DATA_PARAM_EMPTY_LINE } from '../../../../constants/html'
import { isValueFunctionType } from '../../../../utils/common'
import { linkifyHtml } from '../../../../utils/html'
import { getDarkContentHtml } from '../../../../utils/themeContent'
import { newLineToBrInEmailText } from '../../Email.utils'

import {
  EMAIL_TEXT_MIN_CONTRAST_RATIO,
  NAMED_COLORS_RGB,
  RE_BODY,
  RE_BODY_BGCOLOR_ATTR,
  RE_BODY_STYLE_ATTR,
  RE_BODY_TAG,
  RE_EMAIL_ANGLE,
  RE_EMAIL_PLAIN,
  RE_HEX_COLOR,
  RE_IMPORTANT_SUFFIX,
  RE_RGB_COLOR,
  RE_SAFE_COLOR,
  RE_SEPARATOR,
  RE_STYLE_BACKGROUND,
  RE_STYLE_COLOR,
  RE_STYLE_COLOR_DECLARATION,
  RE_TIME,
  RE_WS,
  RE_WS_NBSP,
  RE_YEAR,
  RE_ZERO_ALPHA_COLOR,
  RE_ZWS,
  SEE_THROUGH_BACKGROUNDS,
  WHITE_RGB,
} from './EmailProfileMessageBody.constants'
import { MessageText } from './MessageText'

import * as styles from './EmailProfileMessageBody.module.css'

/** ---------- Utility functions ---------- */
function getSafeColorValue(value) {
  const trimmed = value?.trim().replace(RE_IMPORTANT_SUFFIX, EMPTY_STRING)

  return trimmed && RE_SAFE_COLOR.test(trimmed) ? trimmed : null
}

function isSeeThroughBackground(value) {
  const normalized = value
    ?.trim()
    .toLowerCase()
    .replace(RE_IMPORTANT_SUFFIX, EMPTY_STRING)

  return (
    !normalized ||
    SEE_THROUGH_BACKGROUNDS.includes(normalized) ||
    RE_ZERO_ALPHA_COLOR.test(normalized)
  )
}

/** Parses a CSS color into [r, g, b]; returns null for unsupported values. */
function parseCssColorToRgb(value) {
  const normalized = value
    ?.trim()
    .toLowerCase()
    .replace(RE_IMPORTANT_SUFFIX, EMPTY_STRING)

  if (!normalized) {
    return null
  }

  if (NAMED_COLORS_RGB[normalized]) {
    return NAMED_COLORS_RGB[normalized]
  }

  const hexMatch = normalized.match(RE_HEX_COLOR)

  if (hexMatch) {
    const hex = hexMatch[1]

    if (hex.length === 3 || hex.length === 4) {
      return [hex[0], hex[1], hex[2]].map(char => parseInt(char + char, 16))
    }

    if (hex.length === 6 || hex.length === 8) {
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ]
    }

    return null
  }

  const rgbMatch = normalized.match(RE_RGB_COLOR)

  // CSS clamps out-of-range channels like rgb(300, 0, 0) to 255.
  return rgbMatch
    ? rgbMatch.slice(1, 4).map(channel => Math.min(255, Number(channel)))
    : null
}

function getChannelLuminance(channel) {
  const normalized = channel / 255

  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4
}

function getContrastRatio(rgbA, rgbB) {
  const [luminanceA, luminanceB] = [rgbA, rgbB].map(
    ([r, g, b]) =>
      0.2126 * getChannelLuminance(r) +
      0.7152 * getChannelLuminance(g) +
      0.0722 * getChannelLuminance(b)
  )

  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)

  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Resolves the background the node's text is actually rendered on: the
 * nearest own/ancestor declared background, else the message canvas. Returns
 * null when a background is declared but can't be parsed — in that case the
 * readability of the text can't be judged and it must be left untouched.
 */
function getEffectiveBackgroundRgb(domNode, canvasRgb) {
  let currentNode = domNode

  while (currentNode) {
    const attribs = currentNode.attribs

    const declaredBackground =
      attribs?.style?.match(RE_STYLE_BACKGROUND)?.[1]?.trim() ||
      attribs?.bgcolor?.trim()

    if (!isSeeThroughBackground(declaredBackground)) {
      return parseCssColorToRgb(declaredBackground)
    }

    currentNode = currentNode.parent
  }

  return canvasRgb
}

/**
 * Drops an inline text color that is unreadable on its effective background
 * (e.g. `color: white` in an email pasted from a dark-themed client) so the
 * text falls back to the default readable color. Colors that sit on their
 * own dark background (buttons, colored tables) are kept as sent.
 */
function fixUnreadableTextColor(domNode, canvasRgb) {
  const attribs = domNode.attribs

  const styleColor = attribs?.style?.match(RE_STYLE_COLOR)?.[1]
  const fontColor = attribs?.color

  const textRgb = parseCssColorToRgb(styleColor ?? fontColor)

  if (!textRgb) {
    return
  }

  const backgroundRgb = getEffectiveBackgroundRgb(domNode, canvasRgb)

  if (
    !backgroundRgb ||
    getContrastRatio(textRgb, backgroundRgb) >= EMAIL_TEXT_MIN_CONTRAST_RATIO
  ) {
    return
  }

  if (styleColor) {
    attribs.style = attribs.style
      .replace(RE_STYLE_COLOR_DECLARATION, '$1')
      .replace(/;{2,}/g, ';')
      .replace(/^\s*;\s*|\s*;\s*$/g, EMPTY_STRING)
  } else {
    delete attribs.color
  }
}

/**
 * Extracts the background (and, with it, the default text color) declared on
 * the original `<body>` tag. Sanitizing drops the body tag itself, so without
 * this an email designed as light-text-on-dark-body becomes invisible on the
 * white message container. The text color is only kept together with a
 * background — alone it would be applied onto our own white background.
 */
function getBodyPresentationColors(raw) {
  const bodyTagAttributes = raw.match(RE_BODY_TAG)?.[1]

  if (!bodyTagAttributes) {
    return null
  }

  const styleAttrMatch = bodyTagAttributes.match(RE_BODY_STYLE_ATTR)
  const styleAttr = styleAttrMatch?.[1] ?? styleAttrMatch?.[2] ?? EMPTY_STRING

  const backgroundColor =
    getSafeColorValue(styleAttr.match(RE_STYLE_BACKGROUND)?.[1]) ||
    getSafeColorValue(bodyTagAttributes.match(RE_BODY_BGCOLOR_ATTR)?.[1])

  if (isSeeThroughBackground(backgroundColor)) {
    return null
  }

  return {
    backgroundColor,
    color: getSafeColorValue(styleAttr.match(RE_STYLE_COLOR)?.[1]),
  }
}

function wrapHtml(raw) {
  return `<html><body>${newLineToBrInEmailText(raw)}</body></html>`
}

function sanitizeHtml(input) {
  const wrapped = wrapHtml(input)
  const DOMPurify = window?.DOMPurify

  return DOMPurify
    ? DOMPurify.sanitize(wrapped, { USE_PROFILES: { html: true } })
    : wrapped
}

/** ---------- Helpers factory (per-parse) ---------- */
function makeHelpers(opts) {
  const stripEmptyLines = !!opts.stripEmptyLines
  const textCache = new WeakMap()

  /** Recursively collects plain text from a DOM node (memoized). */
  function getNodeText(node) {
    if (!node) {
      return EMPTY_STRING
    }

    if (textCache.has(node)) {
      return textCache.get(node)
    }

    let out = EMPTY_STRING

    if (node.type === 'text') {
      out = node.data ?? EMPTY_STRING
    } else if (Array.isArray(node.children)) {
      out = node.children
        .map(function (c) {
          return getNodeText(c)
        })
        .join(EMPTY_STRING)
    }

    textCache.set(node, out)

    return out
  }

  /** Returns true if the text node is visually empty. */
  function isEmptyText(domNode) {
    if (domNode?.type !== 'text') {
      return false
    }

    const data = domNode.data ?? EMPTY_STRING
    const ws = stripEmptyLines ? RE_WS_NBSP : RE_WS
    const stripped = data
      .replace(ws, EMPTY_STRING)
      .replace(RE_ZWS, EMPTY_STRING)

    return stripped.length === 0
  }

  /** Returns true if text consists only of separators like "-----" or "***". */
  function isOnlySeparator(text) {
    return RE_SEPARATOR.test(text)
  }

  /**
   * Determines if an element is “empty”:
   * - has no child tags
   * - its combined text contains NBSP (\u00A0)
   * - its text consists only of whitespace/NBSP
   */
  function isNbspOnlyElement(node) {
    if (!node || node.type !== 'tag') {
      return false
    }

    if (
      Array.isArray(node.children) &&
      node.children.some(function (c) {
        return c.type === 'tag'
      })
    ) {
      return false
    }

    const text = getNodeText(node)

    return /\u00A0/.test(text) && /^[\s\u00A0]+$/.test(text)
  }

  /** Checks if a node can be skipped during parsing. */
  function isSkippable(node) {
    if (
      !node ||
      (node.type === 'text' && isEmptyText(node)) ||
      (!stripEmptyLines && node.type === 'tag' && isNbspOnlyElement(node))
    ) {
      return true
    }

    const txt = getNodeText(node)

    if (txt && isOnlySeparator(txt)) {
      return true
    }

    return false
  }

  /**
   * Walks through siblings in the specified direction.
   * @param {Object} node - starting node
   * @param {'next'|'prev'} dir - direction
   * @param {Object} options
   * @param {(n:Object)=>boolean} options.match - condition for target node
   * @param {boolean} [options.stopOnMeaningful=false] - stop when meaningful content is found
   */
  function walkSiblings(node, dir, options) {
    const match =
      options && isValueFunctionType(options.match)
        ? options.match
        : function () {
            return true
          }
    const stopOnMeaningful = !!(options && options.stopOnMeaningful)
    const skipSkippable = !!(options && options.skipSkippable)

    let cur = node?.[dir]

    while (cur) {
      if (skipSkippable && isSkippable(cur)) {
        cur = cur[dir]
        continue
      }

      if (match(cur)) {
        return cur
      }

      if (stopOnMeaningful) {
        const txt = getNodeText(cur)

        if ((txt && txt.trim().length > 0) || cur.type === 'tag') {
          break
        }
      }

      cur = cur[dir]
    }

    return null
  }

  /** Heuristic: determines if a node looks like a thread header (time/email/year pattern). */
  function isLikelyThreadHeaderNode(node) {
    const txt = getNodeText(node)

    if (!txt || isOnlySeparator(txt)) {
      return false
    }

    const hasTime = RE_TIME.test(txt)
    const hasEmail = RE_EMAIL_ANGLE.test(txt) || RE_EMAIL_PLAIN.test(txt)
    const hasYear = RE_YEAR.test(txt)

    return hasTime && (hasEmail || hasYear)
  }

  /** Finds the nearest previous header-like node before the given one. */
  function findHeaderBefore(node) {
    return walkSiblings(node, 'prev', {
      match: function (n) {
        return isLikelyThreadHeaderNode(n)
      },
      stopOnMeaningful: true,
    })
  }

  return {
    findHeaderBefore,
    getNodeText,
    isEmptyText,
    isLikelyThreadHeaderNode,
    isNbspOnlyElement,
    walkSiblings,
  }
}

/** Renders a header node and enforces className "message-thread-header". */
function renderHeaderWithClass(headerNode, parseOptions) {
  if (headerNode?.type === 'tag') {
    const baseProps = attributesToProps(headerNode.attribs || EMPTY_OBJECT)
    const className = [styles.messageThreadHeader, baseProps.className]
      .filter(function (v) {
        return Boolean(v)
      })
      .join(' ')

    return createElement(
      headerNode.name || 'div',
      { ...baseProps, className },
      domToReact(headerNode.children || EMPTY_LIST, parseOptions)
    )
  }

  if (headerNode?.type === 'text') {
    return createElement(
      'div',
      { className: styles.messageThreadHeader },
      headerNode.data ?? EMPTY_STRING
    )
  }

  return null
}

/** Builds parse options for html-react-parser with all parsing rules. */
function createParseOptions(flags) {
  const {
    findHeaderBefore,
    isEmptyText,
    isLikelyThreadHeaderNode,
    isNbspOnlyElement,
    walkSiblings,
  } = makeHelpers(flags)

  // Per-parse state (no cross-call leaks)
  const deferredHeaders = new WeakSet()
  const skippedNodes = new WeakSet()

  const parseOptions = {
    replace: function (domNode) {
      // The dark theme corrects the colors before parsing instead
      if (!flags.isDark && domNode?.type === 'tag' && domNode.attribs) {
        fixUnreadableTextColor(domNode, flags.canvasRgb)
      }

      // Add `data-empty-line` attr to empty tag
      if (domNode?.type === 'tag' && isNbspOnlyElement(domNode)) {
        domNode.attribs = {
          ...(domNode.attribs || EMPTY_OBJECT),
          [HTML_DATA_PARAM_EMPTY_LINE]: 'true',
        }
      }

      // Remove empty text nodes
      if (skippedNodes.has(domNode) || isEmptyText(domNode)) {
        return <></>
      }

      // Collapse consecutive <br> tags when cleaning
      if (flags.stripEmptyLines && domNode?.name === 'br') {
        const prevSig = walkSiblings(domNode, 'prev', {
          match: function () {
            return true
          },
        })

        if (prevSig?.name === 'br') {
          return <></>
        }
      }

      // Remove NBSP-only wrappers (except links and quotes)
      if (flags.stripEmptyLines && domNode?.type === 'tag') {
        const tag = domNode.name

        if (tag !== 'blockquote' && tag !== 'a' && isNbspOnlyElement(domNode)) {
          return <></>
        }
      }

      // Defer header rendering if followed by a <blockquote>
      if (isLikelyThreadHeaderNode(domNode)) {
        const nextSig = walkSiblings(domNode, 'next', {
          match: () => true,
          skipSkippable: false,
        })

        if (nextSig?.name === 'blockquote') {
          deferredHeaders.add(domNode)

          return <></>
        }
      }

      // Safe link rendering
      if (domNode?.name === 'a') {
        const props = attributesToProps(domNode.attribs || EMPTY_OBJECT)

        return (
          <a
            {...props}
            className={[styles.messageLink, props.className]
              .filter(function (v) {
                return Boolean(v)
              })
              .join(' ')}
            rel='noopener noreferrer'
            target='_blank'
          >
            {domToReact(domNode.children, parseOptions)}
          </a>
        )
      }

      // Handle <blockquote> and attach nearest header with className
      if (domNode?.name === 'blockquote') {
        const props = attributesToProps(domNode.attribs || EMPTY_OBJECT)
        const headerNode = findHeaderBefore(domNode)

        const headerReact = headerNode
          ? renderHeaderWithClass(headerNode, parseOptions)
          : null

        if (headerNode) {
          skippedNodes.add(headerNode)

          if (deferredHeaders.has(headerNode)) {
            deferredHeaders.delete(headerNode)
          }
        }

        return (
          <MessageText>
            {headerReact}
            <blockquote {...props}>
              {domToReact(domNode.children, parseOptions)}
            </blockquote>
          </MessageText>
        )
      }

      return undefined
    },
  }

  return parseOptions
}

/**
 * Builds the full React node tree for a message body.
 * - Sanitizes (if DOMPurify is available)
 * - Extracts <body>…</body> content
 * - Parses into React elements with custom link and quote logic
 * - Optionally cleans up empty lines and NBSP-only wrappers
 * - Adds "message-thread-header" class to detected thread headers
 *
 * - In the dark theme redraws light backgrounds dark and lightens unreadable
 *   text colors (`getDarkContentHtml`)
 *
 * @param {string} text
 * @param {{ isDark?: boolean, stripEmptyLines?: boolean }} options
 */
export function getMessageTree(
  text,
  { isDark, stripEmptyLines } = EMPTY_OBJECT
) {
  if (!text) {
    return null
  }

  const bodyColors = getBodyPresentationColors(text)

  // A body background that is kept on the wrapper but can't be parsed (e.g.
  // hsl) leaves the canvas unknown (null) — contrast fixes are then skipped.
  const canvasRgb = bodyColors
    ? parseCssColorToRgb(bodyColors.backgroundColor)
    : WHITE_RGB

  const sanitized = sanitizeHtml(linkifyHtml(text))
  const m = sanitized.match(RE_BODY)
  const inner = m ? m[1] : sanitized

  const parseOptions = createParseOptions({
    canvasRgb,
    isDark,
    stripEmptyLines,
  })

  const wrapperStyle = bodyColors
    ? ` style="background-color:${bodyColors.backgroundColor}${bodyColors.color ? `;color:${bodyColors.color}` : EMPTY_STRING}"`
    : EMPTY_STRING

  const html = `<div${wrapperStyle}>${inner}</div>`

  return parse(isDark ? getDarkContentHtml(html) : html, parseOptions)
}
